import { useRef } from 'react';

import { Badge } from '@frontend/components/ui/badge';
import {
  UserFieldExtensions,
  UserFieldInput,
} from '@frontend/shared/components/UserField/UserFieldInput';
import { useDirectoryUsers } from '@frontend/shared/hooks/useDirectory';
import { X } from 'lucide-react';

export interface UserFieldProps extends UserFieldExtensions {
  selectedIds: string[];
  onlyEditors?: boolean;
  setSelectedIds: (items: string[]) => void;
  forcedIds?: string[];
  includedIds?: string[];
  excludedIds?: string[];
  excludeDeleted?: boolean;
}

export const UserField = ({
  selectedIds,
  setSelectedIds,
  forcedIds,
  includedIds,
  excludedIds,
  placeholder,
  excludeDeleted,
  ...inputProps
}: UserFieldProps) => {
  const selectedUsers = useDirectoryUsers(selectedIds);
  const forcedUsers = useDirectoryUsers(forcedIds ?? []);
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div
      className="py-1 flex flex-wrap items-center border-b border-gray-300 w-full relative focus-within:border-blue-400 cursor-text"
      onClick={() => {
        inputRef.current?.focus();
      }}
    >
      {forcedUsers.map((item) => (
        <div key={item?.id} className="h-8 mr-2 flex items-center">
          <Badge variant="secondary">{item?.name}</Badge>
        </div>
      ))}
      {selectedUsers.map((item) => (
        <div key={item?.id} className="h-8 mr-2 flex items-center">
          <Badge variant="secondary" className="gap-1">
            {item?.name}
            <button
              disabled={inputProps.isDisabled}
              onClick={() =>
                setSelectedIds(selectedIds.filter((id) => id !== item?.id))
              }
              className="ml-1 hover:bg-gray-300 rounded-full p-0.5 disabled:opacity-50"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        </div>
      ))}

      <UserFieldInput
        ref={inputRef}
        includedIds={includedIds}
        excludedIds={excludedIds}
        excludeDeleted={excludeDeleted}
        {...inputProps}
        onSelectId={(id) => {
          setSelectedIds(selectedIds.concat([id]));
        }}
        onKeyDown={(e) => {
          if (
            e.key === 'Backspace' &&
            selectedIds.length &&
            !e.currentTarget.value
          ) {
            setSelectedIds(selectedIds.slice(0, -1));
          }
        }}
        selectedIds={selectedIds}
        placeholder={placeholder}
      />
    </div>
  );
};
